// forked from https://github.com/uiwjs/react-codemirror/tree/v3/src

import React, {
	useRef,
	useEffect,
	useImperativeHandle,
	useState,
	useMemo
} from "react";
import CodeMirror from "codemirror";
import "codemirror/addon/hint/anyword-hint.js";
import "codemirror/addon/hint/show-hint.js";
import "codemirror/addon/hint/show-hint.css";
import "codemirror/mode/meta";
import "./index.css";
import { IReactCodemirror } from "./interface";

// define custom mode for zFormulaEditor
CodeMirror.defineMode("zFormulaEditorCustomMode", function () {
	return {
		startState: function () {
			return {
				inFunction: false,
				inParams: false,
				inString: false,
				stringDelim: null
			};
		},
		token: function (stream: CodeMirror.StringStream, state: any) {
			if (stream.match("if", false)) {
				stream.next();
				stream.next();
				state.inFunction = true;
				state.functionName = "if";
				return "keyword";
			}
			if (state.inFunction) {
				if (stream.peek() === "(") {
					stream.next();
					state.inParams = true;
					return "bracket";
				} else if (stream.peek() === ")") {
					stream.next();
					state.inParams = false;
					state.inFunction = false;
					state.functionName = "";
					return "bracket";
				} else if (state.inString) {
					if (stream.peek() === state.stringDelim) {
						// Check for end of string
						stream.next(); // Consume the closing quote
						state.inString = false; // Reset string state
						return "string"; // Style class for strings
					}
					while (state.inString && !stream.eol()) {
						if (
							stream.next() === state.stringDelim &&
							!stream.eat("\\")
						) {
							state.inString = false; // End string if not escaped
						}
					}
					return "string";
				} else if (state.inParams) {
					if (stream.match(/^(\'|\")/)) {
						state.inString = true;
						state.stringDelim = stream.current(); // Remember the quote type
						return "string";
					} else if (stream.match(/true|false|True|False/)) {
						return "number";
					} else if (stream.match(/[a-zA-Z_][a-zA-Z0-9_]*/)) {
						return "variable";
					} else if (stream.match(/(\d+(\.\d+)?)/)) {
						return "number";
					} else if (stream.match(/and|or/)) {
						return "logic-operator"; // Return a custom style class
					} else if (stream.match(/[+\-*\/=]/)) {
						return "operator"; // Use the "operator" CSS class for styling
					} else if (stream.match(/(==|!=|>=|<=)/)) {
						return "operator";
					} else {
						stream.next();
						return null;
					}
				} else {
					stream.next();
					return null;
				}
			} else if (state.inString) {
				if (stream.peek() === state.stringDelim) {
					// Check for end of string
					stream.next(); // Consume the closing quote
					state.inString = false; // Reset string state
					return "string"; // Style class for strings
				}
				while (state.inString && !stream.eol()) {
					if (
						stream.next() === state.stringDelim &&
						!stream.eat("\\")
					) {
						state.inString = false; // End string if not escaped
					}
				}
				return "string";
			} else if (stream.match("if", false)) {
				stream.next();
				stream.next();
				state.inFunction = true;
				state.functionName = "if";
				return "keyword";
			} else if (stream.match(/^(\'|\")/)) {
				state.inString = true;
				state.stringDelim = stream.current(); // Remember the quote type
				return "string";
			} else if (stream.match(/true|false|True|False/)) {
				return "number";
			} else if (stream.match(/[a-zA-Z_][a-zA-Z0-9_]*/)) {
				return "variable";
			} else if (stream.match(/(\d+(\.\d+)?)/)) {
				return "number";
			} else if (stream.match(/and|or/)) {
				return "logic-operator"; // Return a custom style class
			} else if (stream.match(/[+\-*\/=]/)) {
				return "operator"; // Use the "operator" CSS class for styling
			} else if (stream.match(/(==|!=|>=|<=)/)) {
				return "operator";
			} else {
				stream.next();
				return null;
			}
		}
	};
});

export { CodeMirror };

const defaultOptions = {
	tabSize: 2,
	autoCloseBrackets: true,
	matchBrackets: true,
	showCursorWhenSelecting: true,
	lineNumbers: true,
	fullScreen: true
};

const ReactCodeMirror: React.FC<IReactCodemirror> = (props = {}, ref) => {
	const {
		options = {},
		value = "",
		width = "100%",
		height = "100%",
		lazyLoadMode = true
	} = props;
	const [editor, setEditor] = useState();
	const textareaRef = useRef();
	const lastestProps = useRef(props);

	useImperativeHandle(
		ref,
		() => ({ editor, textarea: textareaRef.current }),
		[editor, textareaRef]
	);
	lastestProps.current = props;

	// 将props中所有的事件处理函数映射并保存
	function getEventHandleFromProps() {
		const propNames = Object.keys(props);
		const eventHandle = propNames.filter(keyName => {
			return /^on+/.test(keyName);
		});

		const eventDict = {};
		eventHandle.forEach(ele => {
			const name = ele.slice(2);
			if (name && name[0]) {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				eventDict[ele] = name.replace(name[0], name[0].toLowerCase());
			}
		});

		return eventDict;
	}

	// http://codemirror.net/doc/manual.html#config
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	async function setOptions(instance, opt = {}) {
		if (typeof opt === "object" && window) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			const mode = CodeMirror.findModeByName(opt.mode || "");
			if (lazyLoadMode && mode && mode.mode) {
				await import(`codemirror/mode/${mode.mode}/${mode.mode}.js`);
			}
			if (mode) {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				opt.mode = mode.mime;
			}
			Object.keys(opt).forEach(name => {
				if (
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					(opt[name] || opt[name] === false) &&
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					JSON.stringify(opt[name])
				) {
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					instance.setOption(name, opt[name]);
				}
			});
		}
	}

	useEffect(() => {
		if (!editor && window) {
			// 生成codemirror实例
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			const instance = CodeMirror.fromTextArea(textareaRef.current, {
				...defaultOptions,
				...options
			});
			const eventDict = getEventHandleFromProps();
			Object.keys(eventDict).forEach(event => {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				instance.on(eventDict[event], (...params) =>
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					lastestProps.current[event](...params)
				);
			});
			instance.setValue(value || "");

			if (width || height) {
				// 设置尺寸
				instance.setSize(width, height);
			}
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			setEditor(instance);
			setOptions(instance, { ...defaultOptions, ...options });
		}
		return () => {
			if (editor && window) {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				editor.toTextArea();
				setEditor(undefined);
			}
		};
	}, []);

	useMemo(() => {
		if (!editor || !window) return;
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const val = editor.getValue();
		if (value !== undefined && value !== val) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			editor.setValue(value);
		}
	}, [value]);

	useMemo(() => {
		if (!editor || !window) return;
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		editor.setSize(width, height);
	}, [width, height]);

	useMemo(() => {
		if (!editor || !window) return;
		setOptions(editor, { ...defaultOptions, ...options });
	}, [editor, options]);

	return (
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		<textarea ref={textareaRef}>{value}</textarea>
	);
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default React.forwardRef(ReactCodeMirror);
